
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/contexts/auth";
import { Navigate } from "react-router-dom";
import SocialAuthButtons from "./SocialAuthButtons";
import { useTranslation } from "react-i18next";
import useFeatureFlag from "@/hooks/useFeatureFlag";
import { getPhoneAuthErrorMessage, getPhoneAuthSettings, sendPhoneOtp, verifyPhoneOtp } from "@/services/nativePhoneAuthService";
import { Mail, MessageCircle } from "lucide-react";
import { cn } from "@/lib/utils";

const authTabListClassName = "grid h-auto w-full grid-cols-2 rounded-md border border-brand-cloud bg-white p-1 shadow-sm";
const authMethodButtonClassName = "flex h-10 items-center justify-center gap-2 rounded-sm border border-transparent px-4 text-center text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-slate focus-visible:ring-offset-2";
const authMethodButtonActiveClassName = "bg-brand-mist text-brand-ink shadow-none";
const authMethodButtonIdleClassName = "bg-transparent text-brand-slate hover:bg-brand-mist hover:text-brand-ink";

interface LoginFormProps {
  isSubmitting: boolean;
  setIsSubmitting: (value: boolean) => void;
  redirectPath?: string;
}

const LoginForm = ({ isSubmitting, setIsSubmitting, redirectPath = "/" }: LoginFormProps) => {
  const { t } = useTranslation('auth');
  const { signIn, user, refreshSession } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneOtp, setPhoneOtp] = useState("");
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [phoneInfo, setPhoneInfo] = useState<string | null>(null);
  const [phoneSubmitting, setPhoneSubmitting] = useState(false);
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [loginMethod, setLoginMethod] = useState<"email" | "phone">("email");
  const [isPhoneAuthReady, setIsPhoneAuthReady] = useState(false);
  const [isCheckingPhoneAuth, setIsCheckingPhoneAuth] = useState(false);
  const [phoneAuthStatus, setPhoneAuthStatus] = useState<string | null>(null);
  const enablePhoneLogin = useFeatureFlag("ENABLE_PHONE_LOGIN");

  const formSchema = z.object({
    email: z.string().min(1, t("validation.emailRequired")).email(t("validation.emailInvalid")),
    password: z.string().min(1, t("validation.passwordRequired")),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  // If user is already authenticated, redirect
  if (user) {
    return <Navigate to={redirectPath} replace />;
  }

  useEffect(() => {
    if (resendCountdown <= 0) {
      return;
    }

    const timer = window.setTimeout(() => {
      setResendCountdown((current) => current - 1);
    }, 1000);

    return () => {
      window.clearTimeout(timer);
    };
  }, [resendCountdown]);

  useEffect(() => {
    if (!enablePhoneLogin) {
      setLoginMethod("email");
      setIsPhoneAuthReady(false);
      setPhoneAuthStatus(null);
      return;
    }

    let active = true;

    const loadPhoneAuthSettings = async () => {
      setIsCheckingPhoneAuth(true);
      setPhoneAuthStatus(null);

      try {
        const settings = await getPhoneAuthSettings();

        if (!active) {
          return;
        }

        setIsPhoneAuthReady(settings.phoneEnabled);
        if (!settings.phoneEnabled) {
          setPhoneAuthStatus("WhatsApp login is enabled in app flags, but disabled in Supabase Auth Providers for this project.");
          setLoginMethod("email");
        }
      } catch (caughtError) {
        if (!active) {
          return;
        }

        const message = caughtError instanceof Error ? caughtError.message : "Failed to load WhatsApp login settings";
        setIsPhoneAuthReady(false);
        setPhoneAuthStatus(message);
        setLoginMethod("email");
      } finally {
        if (active) {
          setIsCheckingPhoneAuth(false);
        }
      }
    };

    void loadPhoneAuthSettings();

    return () => {
      active = false;
    };
  }, [enablePhoneLogin]);

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      const result = await signIn(values.email, values.password);
      
      // Check if there was an error in the result
      if (result.error) {
        setError(result.error);
        setIsSubmitting(false);
        return;
      }
      
      // If successful, the redirect will be handled by the auth context
    } catch (error: unknown) {
      console.error("Login error:", error);
      const message = error instanceof Error ? error.message : 'An error occurred during login';
      setError(message);
      setIsSubmitting(false);
    }
  };

  const handleSendPhoneOtp = async () => {
    setPhoneSubmitting(true);
    setPhoneError(null);
    setPhoneInfo(null);

    try {
      const { error: otpError } = await sendPhoneOtp(phoneNumber);

      if (otpError) {
        throw otpError;
      }

      setPhoneOtpSent(true);
      setResendCountdown(60);
      setPhoneInfo("A one-time code has been sent to your WhatsApp login number.");
    } catch (caughtError) {
      const message = getPhoneAuthErrorMessage(caughtError);
      setPhoneError(message);
    } finally {
      setPhoneSubmitting(false);
    }
  };

  const handleVerifyPhoneOtp = async () => {
    setPhoneSubmitting(true);
    setPhoneError(null);
    setPhoneInfo(null);

    try {
      const { error: verifyError } = await verifyPhoneOtp(phoneNumber, phoneOtp);

      if (verifyError) {
        throw verifyError;
      }

      setPhoneInfo('WhatsApp login successful. Redirecting...');
      await refreshSession();
    } catch (caughtError) {
      const message = getPhoneAuthErrorMessage(caughtError);
      setPhoneError(message);
    } finally {
      setPhoneSubmitting(false);
    }
  };

  const emailLoginForm = (
    <div className="space-y-4 rounded-xl border border-brand-cloud bg-white p-5 shadow-sm">
      

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {error && (
          <div className="rounded-md bg-red-50 p-3 text-center text-sm text-coral">
            {error}
          </div>
        )}

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("login.email")}</FormLabel>
              <FormControl>
                <Input placeholder={t("login.emailPlaceholder")} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("login.password")}</FormLabel>
              <FormControl>
                <Input type="password" placeholder={t("login.passwordPlaceholder")} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? t("login.loggingIn") : t("login.loginButton")}
        </Button>
        </form>
      </Form>
    </div>
  );

  const phoneLoginForm = (
    <div className="space-y-4 rounded-xl border border-brand-cloud bg-white p-5 shadow-sm">
      

      {phoneError && (
        <Alert variant="destructive">
          <AlertDescription>{phoneError}</AlertDescription>
        </Alert>
      )}

      {phoneInfo && (
        <Alert>
          <AlertDescription>{phoneInfo}</AlertDescription>
        </Alert>
      )}

      {!isPhoneAuthReady && phoneAuthStatus && (
        <Alert variant="destructive">
          <AlertDescription>{phoneAuthStatus}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label htmlFor="phone-login-number">WhatsApp number</Label>
        <div className="flex h-10 items-center overflow-hidden rounded-md border border-input bg-background">
          <span className="shrink-0 whitespace-nowrap border-r border-input px-3 text-sm text-muted-foreground">HK +852</span>
          <Input
            className="min-w-0 flex-1 border-0 shadow-none focus-visible:ring-0"
            id="phone-login-number"
            inputMode="tel"
            onChange={(event) => setPhoneNumber(event.target.value)}
            placeholder="8-digit HK mobile number"
            value={phoneNumber}
          />
        </div>
        <p className="text-sm text-brand-slate">
          You can still enter a full number like +65... for non-Hong Kong WhatsApp numbers.
        </p>
      </div>

      {phoneOtpSent && (
        <div className="space-y-2">
          <Label htmlFor="phone-login-otp">One-time code</Label>
          <div className="flex justify-center sm:justify-start">
            <InputOTP id="phone-login-otp" maxLength={6} onChange={setPhoneOtp} value={phoneOtp}>
              <InputOTPGroup className="gap-2">
                {Array.from({ length: 6 }).map((_, index) => (
                  <InputOTPSlot key={index} index={index} />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2 sm:flex-row">
        <Button
          className="flex-1"
          disabled={phoneSubmitting || !phoneNumber || resendCountdown > 0 || !isPhoneAuthReady}
          onClick={handleSendPhoneOtp}
          type="button"
          variant="outline"
        >
          {phoneSubmitting && !phoneOtpSent
            ? "Sending code..."
            : resendCountdown > 0
              ? `Resend in ${resendCountdown}s`
              : phoneOtpSent
                ? "Resend code"
                : "Send code"}
        </Button>
        <Button
          className="flex-1"
          disabled={phoneSubmitting || phoneOtp.length !== 6 || !phoneOtpSent || !isPhoneAuthReady}
          onClick={handleVerifyPhoneOtp}
          type="button"
        >
          {phoneSubmitting && phoneOtpSent ? "Verifying..." : "Sign in with WhatsApp"}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {enablePhoneLogin ? (
        <div className="space-y-4">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-brand-slate">
              Choose how you want to sign in
            </p>
            <div className={authTabListClassName}>
              <button
                type="button"
                onClick={() => setLoginMethod("email")}
                aria-pressed={loginMethod === "email"}
                className={cn(
                  authMethodButtonClassName,
                  loginMethod === "email"
                    ? authMethodButtonActiveClassName
                    : authMethodButtonIdleClassName
                )}
              >
                <Mail className="h-4 w-4" />
                <span>Email</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  if (isPhoneAuthReady) {
                    setLoginMethod("phone");
                  }
                }}
                disabled={!isPhoneAuthReady}
                aria-pressed={loginMethod === "phone" && isPhoneAuthReady}
                className={cn(
                  authMethodButtonClassName,
                  loginMethod === "phone" && isPhoneAuthReady
                    ? authMethodButtonActiveClassName
                    : authMethodButtonIdleClassName,
                  isPhoneAuthReady
                    ? null
                    : "cursor-not-allowed opacity-60"
                )}
              >
                <MessageCircle className="h-4 w-4" />
                <span>WhatsApp</span>
              </button>
            </div>
          </div>

          {isCheckingPhoneAuth ? (
            <div className="rounded-md border border-brand-cloud bg-brand-mist p-4 text-sm text-brand-slate">
              Checking WhatsApp login availability...
            </div>
          ) : null}

          {!isCheckingPhoneAuth && phoneAuthStatus && !isPhoneAuthReady ? (
            <Alert variant="destructive">
              <AlertDescription>{phoneAuthStatus}</AlertDescription>
            </Alert>
          ) : null}

          {loginMethod === "email" ? emailLoginForm : phoneLoginForm}
        </div>
      ) : (
        emailLoginForm
      )}
      
      <SocialAuthButtons isSubmitting={isSubmitting} />
    </div>
  );
};

export default LoginForm;
